import { z } from 'zod';
import type { NotificationMessageContext } from 'src/api/generated';
import { schema as messageContextWireSchema } from '../../../api/generated/schemas/NotificationMessageContextWire';
import type { SocketPayloadData } from '../types/notification-realtime.types';
import {
  NOTIFICATION_CHANNEL,
  NOTIFICATION_EVENT_KEY,
  NOTIFICATION_TYPE,
} from '../types/notification.types';

const messageContextValidator = z.fromJSONSchema(messageContextWireSchema, {
  defaultTarget: 'openapi-3.0',
});

/** Validates the generated canonical context without a private browser dependency. */
const messageContextSchema = z.custom<NotificationMessageContext>(
  (value) => messageContextValidator.safeParse(value).success
);

/**
 * Validates the existing untyped socket data envelope before presentation.
 * The shared inbox model uses Dates; this wire adapter requires serialized timestamps
 * and retains the existing optional export fields. Message context comes from the
 * generated canonical validator. Replace the envelope adapter when the backend
 * publishes a complete notification socket-envelope contract.
 */
export const notificationRealtimePayloadSchema = z.object({
  item: z
    .object({
      id: z.string().min(1),
      tenantId: z.string().min(1),
      userId: z.string().min(1),
      title: z.string(),
      body: z.string(),
      channel: z.enum(NOTIFICATION_CHANNEL),
      type: z.enum(NOTIFICATION_TYPE),
      createdAt: z.iso.datetime({ offset: true }),
      readAt: z.iso.datetime({ offset: true }).optional(),
      metadata: z
        .object({
          funnelConversationId: z.number().int().optional(),
          messagingConversationId: z.string().optional(),
          deepLink: z.string().optional(),
          links: z
            .array(z.object({ label: z.string(), url: z.string() }))
            .optional(),
          message: messageContextSchema.optional(),
        })
        .optional(),
    })
    .optional(),
  eventKey: z.enum(NOTIFICATION_EVENT_KEY).optional(),
  inApp: z
    .object({
      enabled: z.boolean().optional(),
      showAlert: z.boolean(),
      playSound: z.boolean(),
    })
    .optional(),
  export: z
    .object({ fileId: z.string().optional(), requestId: z.string().optional() })
    .optional(),
}) satisfies z.ZodType<SocketPayloadData>;
